import z from 'zod'

type RangeId = keyof (typeof import('@sofa-platform/range-data-generator/type-object'))['sofaTypes']

const defaultFilterValues = {
  hasRemovableCover: false,
  hasSofabed: false,
  hasStorage: false,
  hasRecliningBackrest: false,
  isModular: false,
  width: 'all',
} as const

const FILTER_SESSION_STORAGE_KEY = 'start-gallery-filter'

export function getFilterFromSessionStorage(): StartGalleryFilter {
  const filter = window.sessionStorage.getItem(FILTER_SESSION_STORAGE_KEY)
  if (!filter) return defaultFilterValues

  const result = startGallerySessionFilterSchema.safeParse(JSON.parse(filter))
  if (!result.success) return defaultFilterValues
  return result.data
}

export function setFilterInSessionStorage(filter: StartGalleryFilter) {
  const validatedFilter = startGallerySessionFilterSchema.parse(filter)
  window.sessionStorage.setItem(FILTER_SESSION_STORAGE_KEY, JSON.stringify(validatedFilter))
}

const startGallerySessionFilterSchema = z.object({
  hasRemovableCover: z.boolean().default(defaultFilterValues.hasRemovableCover),
  hasSofabed: z.boolean().default(defaultFilterValues.hasSofabed),
  hasStorage: z.boolean().default(defaultFilterValues.hasStorage),
  hasRecliningBackrest: z.boolean().default(defaultFilterValues.hasRecliningBackrest),
  isModular: z.boolean().default(defaultFilterValues.isModular),
  width: z.enum(['all', 's', 'm', 'l']).default(defaultFilterValues.width),
})

export const startGalleryFilterSchema = startGallerySessionFilterSchema.extend({
  productId: z.union([z.string(), z.number()]).transform(String).optional(),
})

export const MODULAR_RANGES: string[] = [
  'vimle',
  'finnala',
  'soderhamn',
  'kivik',
  'jattebo',
  'lillehem',
  'uppakra',
] satisfies RangeId[]

export type StartGalleryFilter = z.infer<typeof startGalleryFilterSchema>

// All bounds in millimeters
export const widthOptions: Record<
  StartGalleryFilter['width'],
  { imperial: { lowerBound: number; upperBound: number }; metric: { lowerBound: number; upperBound: number } }
> = {
  all: {
    imperial: { lowerBound: 0, upperBound: Infinity },
    metric: { lowerBound: 0, upperBound: Infinity },
  },
  l: {
    imperial: { lowerBound: 3048, upperBound: Infinity },
    metric: { lowerBound: 3000, upperBound: Infinity },
  },
  m: {
    imperial: { lowerBound: 2032, upperBound: 3048 },
    metric: { lowerBound: 2000, upperBound: 3000 },
  },
  s: {
    imperial: { lowerBound: 0, upperBound: 2032 },
    metric: { lowerBound: 0, upperBound: 2000 },
  },
}

export const startGalleryFilterSchemaDefaultValues = defaultFilterValues
